import {
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { DataTableShell, FilterBar, PageHeader, SortableTh, useTableSort, useVisibleWindow } from "../../components/ui";
import { useQueryFilters } from "../../lib/useQueryFilters";
import {
  JEV_ACCEPT_LABEL,
  JEV_CATALOG_SEARCH_LIMIT,
  JEV_NEEDS_CONTEXT_COPY,
  JEV_NO_EQUIVALENT_COPY,
  JEV_OUTCOME_ERROR,
  JEV_OUTCOME_NEEDS_CONTEXT,
  JEV_OUTCOME_NO_EQUIVALENT,
  JEV_SKILL_SELECT_LIMIT,
  JEV_SUGGEST_ERROR,
  JEV_SUGGEST_LABEL,
  JEV_SUGGESTION_COLUMN,
  JEV_SUGGESTS_PREFIX,
  JEV_UNMAPPED_LIMIT,
} from "../../constants/adminSkills";
import { adminApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { catalogFuzzyFilter, fuzzyScoreText } from "../../lib/catalogFuzzyFilter";
import type { AdminJevSuggestion, AdminUnmappedSkill } from "../../types/api";

const SKILL_CATEGORIES = [
  { value: "soft", label: "Soft skills" },
  { value: "language_skill", label: "Language (hiring signal)" },
  { value: "office", label: "Office & productivity" },
  { value: "tools_bpo", label: "BPO / support tools" },
  { value: "domain_bpo", label: "BPO / customer ops" },
  { value: "domain_sales", label: "Sales & GTM" },
  { value: "domain_bfsi", label: "BFSI / finance ops" },
  { value: "domain_ops", label: "Ops / logistics / retail" },
  { value: "domain_hr", label: "HR / admin" },
  { value: "domain_creative", label: "Design / marketing" },
  { value: "domain_tech", label: "Tech / engineering" },
  { value: "domain_healthcare", label: "Healthcare / pharma" },
];

type CreateForm = {
  label: string;
  category: string | null;
  code: string;
  description: string;
  aliases: string;
  mapAlias: string | null;
};

const emptyCreateForm = (): CreateForm => ({
  label: "",
  category: "domain_tech",
  code: "",
  description: "",
  aliases: "",
  mapAlias: null,
});

function suggestedSkillCode(suggestion: AdminJevSuggestion | null | undefined): string | null {
  if (!suggestion?.skill_code) return null;
  if (
    suggestion.outcome === JEV_OUTCOME_NO_EQUIVALENT ||
    suggestion.outcome === JEV_OUTCOME_NEEDS_CONTEXT ||
    suggestion.outcome === JEV_OUTCOME_ERROR
  ) {
    return null;
  }
  return suggestion.skill_code;
}

function SuggestionCell({
  row,
  suggesting,
  onSuggest,
  onAccept,
  onCreate,
}: {
  row: AdminUnmappedSkill;
  suggesting: boolean;
  onSuggest: () => void;
  onAccept: (skillCode: string) => void;
  onCreate: () => void;
}) {
  const suggestion = row.jev_suggestion;
  const skillCode = suggestedSkillCode(suggestion);
  return (
    <Stack gap="xs">
      {skillCode && suggestion?.skill_label ? (
        <Group gap="xs" wrap="wrap">
          <Text size="sm">
            {JEV_SUGGESTS_PREFIX}: <strong>{suggestion.skill_label}</strong>
          </Text>
          <Button size="xs" color="violet" onClick={() => onAccept(skillCode)}>
            {JEV_ACCEPT_LABEL}
          </Button>
        </Group>
      ) : suggestion?.outcome === JEV_OUTCOME_NO_EQUIVALENT ? (
        <Group gap="xs" wrap="wrap">
          <Text size="sm">{JEV_NO_EQUIVALENT_COPY}</Text>
          <Button size="xs" variant="default" onClick={onCreate}>
            Create skill
          </Button>
        </Group>
      ) : suggestion && suggestion.outcome !== JEV_OUTCOME_ERROR ? (
        <Text size="sm" c="dimmed">
          {JEV_NEEDS_CONTEXT_COPY}
        </Text>
      ) : suggestion?.outcome === JEV_OUTCOME_ERROR ? (
        <Text size="sm" c="dimmed">
          {suggestion.error?.trim() || JEV_SUGGEST_ERROR}
        </Text>
      ) : null}
      <Button
        size="xs"
        variant="default"
        loading={suggesting}
        onClick={onSuggest}
      >
        {JEV_SUGGEST_LABEL}
      </Button>
    </Stack>
  );
}

const SKILL_FILTER_DEFAULTS = { q: "" };
const SKILL_FILTER_DEBOUNCE_KEYS = ["q"] as const;
const SKILL_FILTER_OPTIONS = { debounceKeys: SKILL_FILTER_DEBOUNCE_KEYS };

export function AdminSkillsPage() {
  const queryClient = useQueryClient();
  const skillFilters = useQueryFilters(SKILL_FILTER_DEFAULTS, SKILL_FILTER_OPTIONS);
  const skillSearch = skillFilters.draft.q;
  const [mapTarget, setMapTarget] = useState<{ alias: string; display_raw: string } | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(emptyCreateForm);

  const unmappedQuery = useQuery({
    queryKey: ["admin-skills-unmapped"],
    queryFn: () => adminApi.listUnmappedSkills({ limit: JEV_UNMAPPED_LIMIT }),
  });

  const skillsQuery = useQuery({
    queryKey: ["admin-skills"],
    queryFn: () => adminApi.listSkills({}),
  });

  const skillOptions = useMemo(
    () =>
      (skillsQuery.data ?? []).map((s) => ({
        value: s.code,
        label: `${s.label} (${s.code})`,
      })),
    [skillsQuery.data],
  );

  const filteredCatalog = useMemo(() => {
    const rows = skillsQuery.data ?? [];
    if (!skillSearch.trim()) return rows.slice(0, JEV_CATALOG_SEARCH_LIMIT);
    return rows
      .map((s) => ({ s, score: fuzzyScoreText(s.label, skillSearch, s.code) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, JEV_CATALOG_SEARCH_LIMIT)
      .map((r) => r.s);
  }, [skillsQuery.data, skillSearch]);

  function invalidateSkillQueries() {
    queryClient.invalidateQueries({ queryKey: ["admin-skills"] });
    queryClient.invalidateQueries({ queryKey: ["admin-skills-unmapped"] });
    queryClient.invalidateQueries({ queryKey: ["skill-catalog"] });
    queryClient.invalidateQueries({ queryKey: ["public-skill-catalog"] });
  }

  const mapMutation = useMutation({
    mutationFn: () =>
      adminApi.mapSkillAlias({
        alias: mapTarget!.alias,
        skill_code: selectedSkill!,
        apply_to_candidates: true,
      }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Alias mapped to catalog skill" });
      setMapTarget(null);
      setSelectedSkill(null);
      invalidateSkillQueries();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not map alias",
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      adminApi.createSkill({
        label: createForm.label.trim(),
        category: createForm.category!,
        code: createForm.code.trim() || undefined,
        description: createForm.description.trim() || undefined,
        aliases: createForm.aliases
          .split(/[,;]/)
          .map((a) => a.trim())
          .filter(Boolean),
        map_alias: createForm.mapAlias || undefined,
      }),
    onSuccess: (res) => {
      notifications.show({
        color: "green",
        message: `Created skill ${res.label} (${res.code})`,
      });
      setCreateOpen(false);
      setCreateForm(emptyCreateForm());
      setMapTarget(null);
      invalidateSkillQueries();
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : "Could not create skill",
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (alias: string) => adminApi.dismissUnmappedSkill(alias),
    onSuccess: () => {
      notifications.show({ color: "gray", message: "Dismissed" });
      queryClient.invalidateQueries({ queryKey: ["admin-skills-unmapped"] });
    },
  });

  const suggestMutation = useMutation({
    mutationFn: (alias: string) => adminApi.suggestUnmappedSkill(alias),
    onSuccess: (row) => {
      queryClient.setQueryData<AdminUnmappedSkill[]>(["admin-skills-unmapped"], (current) => {
        if (!current) return [row];
        return current.map((item) => (item.alias === row.alias ? row : item));
      });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        message: err instanceof ApiError ? err.message : JEV_SUGGEST_ERROR,
      });
    },
  });

  function openCreate(fromUnmapped?: { alias: string; display_raw: string }) {
    setCreateForm({
      ...emptyCreateForm(),
      label: fromUnmapped?.display_raw ?? "",
      aliases: fromUnmapped?.display_raw ?? "",
      mapAlias: fromUnmapped?.alias ?? null,
    });
    setCreateOpen(true);
  }

  const unmapped = unmappedQuery.data ?? [];

  const {
    sorted: sortedUnmapped,
    sortKey: unmappedSortKey,
    direction: unmappedDirection,
    toggle: toggleUnmappedSort,
  } = useTableSort(unmapped, {
    seenAs: (row) => row.display_raw,
    normalized: (row) => row.alias,
    hits: (row) => row.hit_count,
    lastSeen: (row) => new Date(row.last_seen_at),
  });
  const unmappedWindow = useVisibleWindow(sortedUnmapped);

  const {
    sorted: sortedCatalog,
    sortKey: catalogSortKey,
    direction: catalogDirection,
    toggle: toggleCatalogSort,
  } = useTableSort(filteredCatalog, {
    label: (s) => s.label,
    code: (s) => s.code,
    category: (s) => s.category,
  });
  const catalogWindow = useVisibleWindow(sortedCatalog, skillSearch);

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.skills}
        title={NAV_COPY.admin.skills}
        subtitle="Create missing catalog skills, then map rogue free-text aliases. New aliases apply immediately to matching and optionally update existing candidates."
        actions={
          <Button leftSection={<IconPlus size="var(--axio-unit-16)" />} color="violet" onClick={() => openCreate()}>
            Create skill
          </Button>
        }
      />

      <Title order={4}>Rogue / unmapped skills</Title>
      <DataTableShell
        loading={unmappedQuery.isLoading}
        error={
          unmappedQuery.isError
            ? unmappedQuery.error instanceof Error
              ? unmappedQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void unmappedQuery.refetch()}
        hasMore={unmappedWindow.hasMore}
        loadingMore={unmappedWindow.loadingMore}
        onLoadMore={unmappedWindow.loadMore}
        isEmpty={unmapped.length === 0}
        empty={{
          icon: NAV_ICONS.skills,
          title: "No unmapped skills",
          description:
            "They appear here when intake/import resolves a skill that is not in the catalog.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="seenAs" sortKey={unmappedSortKey} direction={unmappedDirection} onSort={toggleUnmappedSort}>
                Seen as
              </SortableTh>
              <SortableTh columnKey="normalized" sortKey={unmappedSortKey} direction={unmappedDirection} onSort={toggleUnmappedSort}>
                Normalized
              </SortableTh>
              <SortableTh columnKey="hits" sortKey={unmappedSortKey} direction={unmappedDirection} onSort={toggleUnmappedSort}>
                Hits
              </SortableTh>
              <SortableTh columnKey="lastSeen" sortKey={unmappedSortKey} direction={unmappedDirection} onSort={toggleUnmappedSort}>
                Last seen
              </SortableTh>
              <Table.Th>{JEV_SUGGESTION_COLUMN}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {unmappedWindow.items.map((row) => (
              <Table.Tr key={row.alias}>
                <Table.Td>{row.display_raw}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {row.alias}
                  </Text>
                </Table.Td>
                <Table.Td className="tabular">{row.hit_count}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {new Date(row.last_seen_at).toLocaleString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <SuggestionCell
                    row={row}
                    suggesting={suggestMutation.isPending && suggestMutation.variables === row.alias}
                    onSuggest={() => suggestMutation.mutate(row.alias)}
                    onAccept={(skillCode) => {
                      setMapTarget({ alias: row.alias, display_raw: row.display_raw });
                      setSelectedSkill(skillCode);
                    }}
                    onCreate={() => openCreate(row)}
                  />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    <Button
                      size="xs"
                      variant="default"
                      onClick={() => {
                        setMapTarget({ alias: row.alias, display_raw: row.display_raw });
                        setSelectedSkill(null);
                      }}
                    >
                      Map to skill
                    </Button>
                    <Button size="xs" variant="default" onClick={() => openCreate(row)}>
                      Create skill
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      loading={dismissMutation.isPending}
                      onClick={() => dismissMutation.mutate(row.alias)}
                    >
                      Dismiss
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Title order={4} mt="md">
        Catalog browser
      </Title>
      <FilterBar>
        <TextInput
          placeholder="Search skills…"
          value={skillSearch}
          onChange={(e) => skillFilters.set({ q: e.currentTarget.value })}
          maw="var(--axio-unit-360)"
        />
      </FilterBar>
      <DataTableShell
        loading={skillsQuery.isLoading}
        error={
          skillsQuery.isError
            ? skillsQuery.error instanceof Error
              ? skillsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void skillsQuery.refetch()}
        hasMore={catalogWindow.hasMore}
        loadingMore={catalogWindow.loadingMore}
        onLoadMore={catalogWindow.loadMore}
        isEmpty={filteredCatalog.length === 0}
        empty={{
          icon: NAV_ICONS.skills,
          title: "No skills match your search",
          description: "Try a different search term or create a new catalog skill.",
        }}
      >
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="label" sortKey={catalogSortKey} direction={catalogDirection} onSort={toggleCatalogSort}>
                Label
              </SortableTh>
              <SortableTh columnKey="code" sortKey={catalogSortKey} direction={catalogDirection} onSort={toggleCatalogSort}>
                Code
              </SortableTh>
              <SortableTh columnKey="category" sortKey={catalogSortKey} direction={catalogDirection} onSort={toggleCatalogSort}>
                Category
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {catalogWindow.items.map((s) => (
              <Table.Tr key={s.code}>
                <Table.Td>{s.label}</Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {s.code}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge variant="outline" color="gray">
                    {s.category}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Modal
        opened={!!mapTarget}
        onClose={() => {
          setMapTarget(null);
          setSelectedSkill(null);
        }}
        title="Map rogue skill to catalog"
        centered
      >
        <Stack>
          <Text size="sm">
            Map <strong>{mapTarget?.display_raw}</strong> → a catalog skill. Future resolves and
            Explore matching will use the canonical ID.
          </Text>
          <Select
            label="Catalog skill"
            searchable
            data={skillOptions}
            filter={catalogFuzzyFilter}
            limit={JEV_SKILL_SELECT_LIMIT}
            value={selectedSkill}
            onChange={setSelectedSkill}
            placeholder="Search catalog (typos OK)…"
            nothingFoundMessage="No skills found"
          />
          <Group justify="space-between">
            <Button
              variant="subtle"
              onClick={() => {
                if (mapTarget) openCreate(mapTarget);
              }}
            >
              Create new skill instead
            </Button>
            <Button
              color="violet"
              disabled={!selectedSkill}
              loading={mapMutation.isPending}
              onClick={() => mapMutation.mutate()}
            >
              Save mapping
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateForm(emptyCreateForm());
        }}
        title="Create catalog skill"
        centered
        size="md"
      >
        <Stack>
          <TextInput
            label="Label"
            required
            placeholder="e.g. Freshdesk"
            value={createForm.label}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setCreateForm((f) => ({ ...f, label: next }));
            }}
          />
          <Select
            label="Category"
            required
            data={SKILL_CATEGORIES}
            value={createForm.category}
            onChange={(v) => setCreateForm((f) => ({ ...f, category: v }))}
          />
          <TextInput
            label="Code (optional)"
            description="Auto-generated from the label if left blank"
            placeholder="freshdesk"
            value={createForm.code}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setCreateForm((f) => ({ ...f, code: next }));
            }}
          />
          <TextInput
            label="Aliases (optional)"
            description="Comma-separated alternate names / misspellings"
            placeholder="fresh desk, fresh-desk"
            value={createForm.aliases}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setCreateForm((f) => ({ ...f, aliases: next }));
            }}
          />
          <Textarea
            label="Description (optional)"
            minRows={2}
            value={createForm.description}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setCreateForm((f) => ({ ...f, description: next }));
            }}
          />
          {createForm.mapAlias && (
            <Text size="sm" c="dimmed">
              Will also map rogue alias <strong>{createForm.mapAlias}</strong> and clear it from the
              unmapped list.
            </Text>
          )}
          <Button
            color="violet"
            disabled={!createForm.label.trim() || !createForm.category}
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create skill
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
